import React from 'react';
import * as R from 'ramda';
import { isEqual } from 'lodash';
import { connect } from 'react-redux';
import { withNamespaces } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';
import { withStyles } from '@material-ui/styles';
import {
  Button,
  Grid,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  RadioGroup,
  Divider,
  FormControlLabel,
  Radio,
} from '@material-ui/core';
import logo from 'dataModule/assets/img/HealthBeatsDialogLogo.svg';
import { resourceTypes, withResource, gettersOf } from 'dataModule/store/resources';
import { actionCreators as messageActionCreators } from 'appModule/message/ducks/message';

const styles = (theme) => ({
  dialogPaper: {
    minHeight: '600px',
    minWidth: '654px',
    borderRadius: '5px',
  },
  dialogLogo: {
    maxWidth: '40px',
  },
  dialogTitle: {
    color: '#212121',
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '19px',
    letterSpacing: '0.15000000596046448px',
  },
  dialogDisplayFlex: {
    display: 'flex',
    alignItems: 'center',
    maxHeight: '40px',
    textIndent: 0,
  },
  dialogCloseButton: {
    position: 'relative',
    left: '10px',
    color: 'black',
    maxHeight: '40px',
    maxWidth: '40px',
  },
  dialogContentWrapper: {
    overflow: 'hidden',
  },
  dialogContent: {
    style: 'regular',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '19px',
    letterSpacing: '0.5px',
    color: '#212121',
    paddingTop: '16px',
    paddingBottom: '30px',
  },
  dialogContentRadioLabel: {
    style: 'regular',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '0.25px',
    color: '#212121',
    marginLeft: '16px',
  },
  dialogContentRadioChecked: {
    '&$checked': {
      color: '#FF5722',
    },
  },
  checked: {},
});

@withNamespaces('common', { wait: true })
@withResource([
  { resourceType: resourceTypes.CONFIGS, method: 'retrieveLanguagePreferences', options: { runOnDidMount: true } },
  { resourceType: resourceTypes.USERS, method: 'updateLanguagePreference' },
])
@connect(
  (state) => ({
    currentSession: R.path(['resources', 'users', 'retrieveCurrent'], state),
  }),
  { notify: messageActionCreators.show },
  null,
  {
    // CRITICAL FIX: Only check state slices relevant to this component
    // This prevents re-renders when globalNotification updates every 10 seconds
    areStatesEqual: (next, prev) => {
      const relevantPaths = [
        ['resources', 'users', 'retrieveCurrent'], // Current session
        ['resources', 'configs'], // Language preferences
      ];

      return relevantPaths.every((path) => R.path(path, next) === R.path(path, prev));
    },
  },
)
@withStyles(styles, { index: 1 })
export default class DialogAppLanguagePreference extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedLanguage: sessionStorage.getItem('language') || 'en',
      currentSessionLanguagePreference: null,
      languagePreferencesOption: [],
    };
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Always allow when local state changes
    if (this.state !== nextState) {
      return true;
    }

    // Always allow when dialog open state changes
    if (this.props.open !== nextProps.open) {
      return true;
    }

    const { data } = nextProps;

    // If data is loading, don't update
    if (data && data.status && data.status.loading) {
      return false;
    }

    // Check if critical props changed
    const criticalProps = [
      'open',
      'currentSession',
      // Note: Exclude 't', 'i18n', 'classes' as they change reference unnecessarily
    ];

    const hasCriticalPropsChanged = criticalProps.some((key) => this.props[key] !== nextProps[key]);

    if (hasCriticalPropsChanged) {
      // Update language preferences when currentSession changes
      if (this.props.currentSession !== nextProps.currentSession) {
        const languagePreferences = gettersOf(resourceTypes.CONFIGS).getLanguagePreferences()(nextProps.data);
        const { currentSession } = nextProps;
        const { selectedLanguage } = this.state;

        if (currentSession) {
          const { demographics: { languagePreference: currentSessionLanguagePreference = null } = {} } = currentSession;
          this.setState(
            {
              selectedLanguage: currentSessionLanguagePreference || selectedLanguage,
              currentSessionLanguagePreference: currentSessionLanguagePreference || selectedLanguage,
              languagePreferencesOption: languagePreferences.list,
            },
            () => this.props.i18n.changeLanguage(this.state.selectedLanguage),
          );
        } else {
          const sessionStorageLanguage = sessionStorage.getItem('language');
          this.setState(
            {
              selectedLanguage: sessionStorageLanguage || selectedLanguage,
              currentSessionLanguagePreference: null,
              languagePreferencesOption: languagePreferences.list,
            },
            () => this.props.i18n.changeLanguage(this.state.selectedLanguage),
          );
        }
      }
      return true;
    }

    // Check if data prop reference changed
    if (this.props.data !== nextProps.data) {
      // Check deeper - did the actual content change?
      try {
        const prevConfigs = this.props.data && this.props.data.configs;
        const nextConfigs = nextProps.data && nextProps.data.configs;

        const prevUsers = this.props.data && this.props.data.users;
        const nextUsers = nextProps.data && nextProps.data.users;
        // Deep comparison of actual data
        const configsChanged = !R.equals(prevConfigs, nextConfigs);
        const usersChanged = !R.equals(prevUsers, nextUsers);

        if (configsChanged || usersChanged) {
          // Only update language preferences if configs changed
          if (configsChanged) {
            const languagePreferences = gettersOf(resourceTypes.CONFIGS).getLanguagePreferences()(nextProps.data);
            const { currentSession } = nextProps;
            const { selectedLanguage } = this.state;

            if (currentSession) {
              const {
                demographics: { languagePreference: currentSessionLanguagePreference = null } = {},
              } = currentSession;
              this.setState(
                {
                  selectedLanguage: currentSessionLanguagePreference || selectedLanguage,
                  currentSessionLanguagePreference: currentSessionLanguagePreference || selectedLanguage,
                  languagePreferencesOption: languagePreferences.list,
                },
                () => this.props.i18n.changeLanguage(this.state.selectedLanguage),
              );
            } else {
              const sessionStorageLanguage = sessionStorage.getItem('language');
              this.setState(
                {
                  selectedLanguage: sessionStorageLanguage || selectedLanguage,
                  currentSessionLanguagePreference: null,
                  languagePreferencesOption: languagePreferences.list,
                },
                () => this.props.i18n.changeLanguage(this.state.selectedLanguage),
              );
            }
          }
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error('Error comparing data:', error);
        // If there's an error, allow the update to be safe
        return true;
      }
    }

    return false;
  }

  handleCloseOrReset = (isReset = false) => {
    const {
      toggleAppLanguagePreferenceDialog,
      currentSession: { demographics: { languagePreference = null } = {} } = {},
    } = this.props;
    const { selectedLanguage } = this.state;

    if (!isReset) {
      toggleAppLanguagePreferenceDialog(false);
    }

    const sessionStorageLanguage = sessionStorage.getItem('language');
    this.setState({ selectedLanguage: sessionStorageLanguage || languagePreference || selectedLanguage });
  };

  handleRadioSelectionChange = async (value) => {
    this.setState({ selectedLanguage: value });
  };

  applyLanguagePreference = async () => {
    const { actionCreators, notify, currentSession, i18n } = this.props;
    const { selectedLanguage } = this.state;

    if (currentSession) {
      actionCreators[resourceTypes.USERS].ajax({
        cargo: { method: 'updateLanguagePreference', input: { content: { languagePreference: selectedLanguage } } },
        onSuccess: ({ data }) => {
          this.handleCloseOrReset(false);

          // Reload Current User's Session
          actionCreators[resourceTypes.USERS].ajax({ cargo: { method: 'retrieveCurrent' } });
        },
        onFailure: ({ error }) => {
          notify({
            message: `Failed to update language preference: ${error.message || 'Unknown error'}`,
            type: 'error',
          });
        },
      });
    } else {
      this.handleCloseOrReset(false);
    }

    // Update i18n language selection and save in local storage
    i18n.changeLanguage(selectedLanguage);
    sessionStorage.setItem('language', selectedLanguage);
    this.setState({ selectedLanguage: selectedLanguage });
  };

  render() {
    const { open, classes, t } = this.props;
    const { selectedLanguage, languagePreferencesOption = [], currentSessionLanguagePreference } = this.state;
    const sessionStorageLanguage = sessionStorage.getItem('language');

    return (
      <Dialog open={open} classes={{ paper: classes.dialogPaper }} aria-labelledby="ihealth-customer-care-info">
        <DialogTitle id="ihealth-customer-care-info" style={{ padding: '12px 16px' }}>
          <Grid container direction="row" style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Grid item className={classes.dialogDisplayFlex}>
              <img src={logo} className={classes.dialogLogo} />
              <span style={{ width: '16px' }} />
              <Typography variant="body1" className={classes.dialogTitle}>
                {t('languagePreferenceDialog.title')}
              </Typography>
            </Grid>
            <Grid item className={classes.dialogDisplayFlex}>
              <IconButton
                aria-label="close"
                className={classes.dialogCloseButton}
                onClick={() => this.handleCloseOrReset(false)}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>

        <Divider />

        <DialogContent className={classes.dialogContentWrapper}>
          <Typography className={classes.dialogContent}>{t('languagePreferenceDialog.description')}</Typography>
          {
            <RadioGroup
              style={{ display: 'flex', flexDirection: 'row' }}
              value={selectedLanguage}
              onChange={(event) => this.handleRadioSelectionChange(event.target.value)}
            >
              {languagePreferencesOption.map((language) => (
                <Grid container alignItems="center" key={language.key} style={{ height: '56px' }}>
                  <FormControlLabel
                    style={{ paddingRight: '310px' }}
                    control={<Radio classes={{ root: classes.dialogContentRadioChecked, checked: classes.checked }} />}
                    label={language.description}
                    key={language.key}
                    value={language.value}
                    labelPlacement="end"
                  />
                  <Divider style={{ color: '#E0E0E0', border: '1px', width: '108%', marginLeft: '-24px' }} />
                </Grid>
              ))}
            </RadioGroup>
          }
        </DialogContent>

        <DialogActions style={{ justifyContent: 'space-between', padding: '10px' }}>
          <Button
            style={{ width: '161px' }}
            variant="contained"
            className="milo-btn-white"
            disabled={
              isEqual(selectedLanguage, currentSessionLanguagePreference) ||
              isEqual(selectedLanguage, sessionStorageLanguage)
            }
            onClick={() => this.handleCloseOrReset(true)}
          >
            {t('languagePreferenceDialog.resetButton')}
          </Button>
          <Button
            style={{ width: '161px' }}
            variant="contained"
            className="milo-btn-orange"
            disabled={
              isEqual(selectedLanguage, currentSessionLanguagePreference) ||
              isEqual(selectedLanguage, sessionStorageLanguage)
            }
            onClick={() => this.applyLanguagePreference()}
          >
            {t('languagePreferenceDialog.applyButton')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}
